import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Chip, Alert, LinearProgress, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { getCategory } from '../api/categoryApi';
import { getQuestionsByDomain } from '../api/questionApi';
import { submitAnswer, getAllPatientReports } from '../api/answersApi';
import { categoryLevels } from './CategoryLevels';
import { usePatient, formatAge } from '../hooks/usePatient';
import { useSnackbar } from './Snackbar';
import { useMe } from '../context/MeContext';
import { getPatientHistory } from '../api/patientApi';
import { getActiveAssessment, finishAssessment } from '../api/saasApi';
import { scoredDomainKeys, isDomainScored, nextUnscoredDomain } from '../utils/assessmentProgress';
import QuestionRow from './assessment/QuestionRow';
import { PageLoader } from './ui';
import { level as levelTokens, layout } from '../theme/tokens';

/** Levels are defined by cumulative question thresholds per domain. */
const getLevelForQuestion = (domainCode, questionNumber) => {
  const domainLevels = categoryLevels.find((c) => c.category === domainCode);
  if (!domainLevels) return null;
  for (const lvl of domainLevels.levels) {
    if (questionNumber <= lvl.maxLevel) return lvl.level;
  }
  return null;
};

/** Sensory and Behaviour are screened for concerns, not scored for achievement. */
const CONCERN_DOMAINS = ['SEN', 'BEH'];

const SAVE_DEBOUNCE_MS = 1500;

const DomainFormScreen = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { billing, isExempt, isPlatformAdmin } = useMe();
  // Payment-gated clinics end the paid assessment when the report is opened.
  const gated = !!billing && !isExempt && !isPlatformAdmin;
  const { patientId, patient, loading: patientLoading, error: patientError } = usePatient();

  const [domain, setDomain] = useState(null);
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [staff, setStaff] = useState({ name: '', id: '' });
  const [lastAssessedAt, setLastAssessedAt] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [cursor, setCursor] = useState({ index: 0, field: 'answer' });
  const [doneOpen, setDoneOpen] = useState(false);
  // "Saved" popup: step 'saved' offers Next domain / report; step 'confirmEnd'
  // warns that opening the report ends the paid assessment.
  const [doneStep, setDoneStep] = useState('saved');
  const [categories, setCategories] = useState([]);
  const [nextDomain, setNextDomain] = useState(null);
  const [scoredCount, setScoredCount] = useState(0);
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [ending, setEnding] = useState(false);
  // Payment gate on entry: a gated clinic may only open a scoring screen while
  // the patient has a Paid (open) assessment. Covers typed/bookmarked URLs and
  // any stray links, not just the save-time 402 from the API.
  const [accessOk, setAccessOk] = useState(!gated);
  const [showKeys, setShowKeys] = useState(false);

  const rowRefs = useRef([]);
  const saveTimer = useRef(null);
  const pendingNavRef = useRef(null);

  // Close the "saved" dialog first, then navigate once its exit transition ends.
  const closeDoneThen = (to) => {
    pendingNavRef.current = to;
    setDoneOpen(false);
  };
  const dirty = useRef(false);

  const isConcernDomain = CONCERN_DOMAINS.includes(code);

  useEffect(() => {
    if (!gated) { setAccessOk(true); return undefined; }
    if (!patientId) return undefined;
    let cancelled = false;
    (async () => {
      let ok = false;
      try {
        const res = await getActiveAssessment(patientId);
        ok = res.data?.data?.status === 'Paid';
      } catch (e) { ok = false; }
      if (cancelled) return;
      if (ok) {
        setAccessOk(true);
      } else {
        showSnackbar('Start and pay for an assessment before scoring.', 'warning');
        navigate(`/patient/${patientId}/assess`, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, patientId]);

  // ---- Load the signed-in clinician ---------------------------------------
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('info') || 'null');
    if (stored?.name) setStaff({ name: stored.name, id: stored.userId });
  }, []);

  // ---- Load domain + questions --------------------------------------------
  useEffect(() => {
    if (!code) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const cats = await getCategory();
        const allCats = cats.data?.data || [];
        const match = allCats.find((d) => d.code === code);
        if (cancelled) return;

        setCategories(allCats);

        setDomain(match || null);
        setDomainsLoaded(true);
        if (!match) return;

        const qs = await getQuestionsByDomain(code);
        if (cancelled) return;

        setQuestions((qs.data?.data || []).map((q) => ({
          id: q.id,
          text: q.question,
          description: q.description?.replace(/\r/g, '') || '',
        })));
      } catch (e) {
        if (!cancelled) {
          setDomainsLoaded(true);
          showSnackbar('Could not load this domain. Check your connection and try again.', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // ---- Seed from the most recent assessment of this domain ----------------
  useEffect(() => {
    if (!patientId || !code) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await getAllPatientReports(patientId);
        if (cancelled || !res.data?.succeeded) return;

        const forDomain = (res.data.data || [])
          .map((r) => ({ createdAt: r.createdAt, data: r.domains?.find((d) => d.domain.code === code) }))
          .filter((r) => r.data)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const latest = forDomain[0];
        if (!latest) return;

        setLastAssessedAt(latest.createdAt);
        setResponses(
          (latest.data.answers || []).reduce((acc, a) => {
            acc[a.questionId] = { answer: a.staffResponse, pr: a.parentResponse };
            return acc;
          }, {})
        );
      } catch (e) {
        // No prior assessment is the normal case for a new patient.
      }
    })();

    return () => { cancelled = true; };
  }, [patientId, code]);

  // ---- Save ----------------------------------------------------------------
  /**
   * Always sends the full answer set, never a delta: the API recomputes
   * skillsAchieved from the request, and upserts per (patient, domain, day) —
   * so repeated saves refine one history entry rather than creating many.
   */
  const persist = useCallback(async (current, { silent = true } = {}) => {
    if (!patientId || !domain) return false;

    const answers = Object.entries(current)
      .filter(([, r]) => r.answer !== undefined || r.pr !== undefined)
      .map(([id, r]) => ({
        questionId: Number(id),
        staffResponse: r.answer,
        parentResponse: r.pr,
      }));

    if (answers.length === 0) return false;

    setSaveState('saving');
    try {
      const res = await submitAnswer({
        patientId: Number(patientId),
        staffName: staff.name,
        createdBy: staff.id,
        domain,
        answers,
      });

      // Per-assessment payment gate. A non-exempt clinic must start and pay for
      // an assessment before answers can be saved; the API returns code 402 when
      // there is no paid assessment in progress. That can happen if the clinician
      // opened a scoring URL directly, or the paid assessment was already
      // finished. Bounce them to the pay-to-start screen instead of leaving a
      // silent "Not saved".
      if (res.data?.code === 402) {
        setSaveState('error');
        showSnackbar('Payment required — start and pay for this assessment first.', 'warning');
        navigate(`/patient/${patientId}/assess`);
        dirty.current = false;
        return false;
      }

      const ok = res.data?.data?.success ?? res.data?.succeeded;
      setSaveState(ok ? 'saved' : 'error');
      if (!ok && !silent) showSnackbar('Could not save your answers.', 'error');
      dirty.current = false;
      return Boolean(ok);
    } catch (e) {
      // Some deployments surface the gate as an HTTP 402 rather than a body code.
      if (e?.response?.status === 402 || e?.response?.data?.code === 402) {
        setSaveState('error');
        showSnackbar('Payment required — start and pay for this assessment first.', 'warning');
        navigate(`/patient/${patientId}/assess`);
        return false;
      }
      setSaveState('error');
      if (!silent) showSnackbar('Could not save your answers.', 'error');
      return false;
    }
  }, [patientId, domain, staff, showSnackbar, navigate]);

  const scheduleSave = useCallback((next) => {
    dirty.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), SAVE_DEBOUNCE_MS);
  }, [persist]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // Losing a half-finished domain to a stray tab close is the failure the old
  // paginated form invited; warn while there's an unsaved edit outstanding.
  useEffect(() => {
    const warn = (e) => {
      if (!dirty.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  // ---- Scoring -------------------------------------------------------------
  const setScore = useCallback((questionId, field, value) => {
    setResponses((prev) => {
      const next = { ...prev, [questionId]: { ...(prev[questionId] || {}), questionId, [field]: value } };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const mirror = useCallback((questionId) => {
    setResponses((prev) => {
      const current = prev[questionId];
      if (!current || current.answer === undefined) return prev;
      const next = { ...prev, [questionId]: { ...current, pr: current.answer } };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  /** Clinician → parent → next question's clinician. */
  const advance = useCallback((index, field) => {
    if (field === 'answer') {
      setCursor({ index, field: 'pr' });
      return;
    }
    const nextIndex = Math.min(index + 1, questions.length - 1);
    setCursor({ index: nextIndex, field: 'answer' });
    rowRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [questions.length]);

  // ---- Keyboard scoring ----------------------------------------------------
  // 1/2/3 scores the focused cell and moves on. Two keystrokes per question,
  // no mouse travel — the difference between a 90-click domain and a fast one.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const q = questions[cursor.index];
      if (!q) return;

      const scoreKeys = { 1: 2, 2: 1, 3: 0 };  // 1=Yes, 2=Emerging, 3=No
      if (e.key in scoreKeys) {
        e.preventDefault();
        setScore(q.id, cursor.field, scoreKeys[e.key]);
        advance(cursor.index, cursor.field);
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const next = Math.min(Math.max(cursor.index + delta, 0), questions.length - 1);
        setCursor({ index: next, field: 'answer' });
        rowRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (e.key === 'ArrowRight') { e.preventDefault(); setCursor((c) => ({ ...c, field: 'pr' })); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); setCursor((c) => ({ ...c, field: 'answer' })); }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cursor, questions, setScore, advance]);

  // ---- Derived -------------------------------------------------------------
  const answeredCount = useMemo(
    () => questions.filter((q) => {
      const r = responses[q.id];
      return r && r.answer !== undefined && r.pr !== undefined;
    }).length,
    [questions, responses]
  );

  const achievedCount = useMemo(
    () => questions.filter((q) => responses[q.id]?.answer === 2 && responses[q.id]?.pr === 2).length,
    [questions, responses]
  );

  /** Questions grouped under their level heading, so progress has structure. */
  const groups = useMemo(() => {
    const out = [];
    questions.forEach((q, i) => {
      const lvl = getLevelForQuestion(code, i + 1);
      const last = out[out.length - 1];
      if (last && last.level === lvl) last.items.push({ q, index: i });
      else out.push({ level: lvl, items: [{ q, index: i }] });
    });
    return out;
  }, [questions, code]);

  const finish = async () => {
    clearTimeout(saveTimer.current);
    const ok = await persist(responses, { silent: false });
    if (!ok) return;

    // Work out progress within the current paid assessment so the popup can
    // offer the next unscored domain by name. Failures here are non-fatal:
    // the popup still opens, just without a "Next" suggestion.
    let active = null;
    let keys = new Set();
    try {
      const [hist, act] = await Promise.all([
        getPatientHistory(patientId),
        gated ? getActiveAssessment(patientId) : Promise.resolve(null),
      ]);
      const a = act?.data?.data;
      active = a && a.status === 'Paid' ? a : null;
      keys = scoredDomainKeys(hist?.data?.data || [], active);
    } catch (e) { /* non-fatal */ }

    if (domain) { keys.add(domain.name); keys.add(domain.code); }
    setActiveAssessment(active);
    setScoredCount(categories.filter((c) => isDomainScored(keys, c)).length);
    setNextDomain(nextUnscoredDomain(categories, keys, code));
    setDoneStep('saved');
    setDoneOpen(true);
  };

  const reportPath = `/patient/${patientId}/report`;
  const endsAssessment = gated && !!activeAssessment;

  const onViewReport = () => {
    if (endsAssessment) setDoneStep('confirmEnd');
    else closeDoneThen(reportPath);
  };

  const endAssessmentAndOpenReport = async () => {
    if (!activeAssessment) return;
    setEnding(true);
    try {
      const res = await finishAssessment(activeAssessment.id);
      if (res.data?.succeeded === false) {
        showSnackbar(res.data?.messages?.[0] || 'Could not end the assessment.', 'error');
        return;
      }
      setActiveAssessment(null);
      closeDoneThen(reportPath);
    } catch (e) {
      showSnackbar(e?.response?.data?.messages?.[0] || 'Could not end the assessment.', 'error');
    } finally {
      setEnding(false);
    }
  };

  // ---- Render --------------------------------------------------------------
  if (patientError) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="warning">{patientError}</Alert>
        <Button onClick={() => navigate('/patient')}>Back to patients</Button>
      </Stack>
    );
  }

  if (domainsLoaded && !domain) return <Navigate to="/404" replace />;
  if (!accessOk || !domainsLoaded || loading || patientLoading) return <PageLoader />;

  const progress = questions.length ? (answeredCount / questions.length) * 100 : 0;

  const saveIndicator = {
    idle: null,
    saving: { icon: <CloudQueueIcon fontSize="small" />, text: 'Saving…', color: 'text.secondary' },
    saved: { icon: <CloudDoneIcon fontSize="small" />, text: 'Saved', color: 'success.main' },
    error: { icon: <ErrorOutlineIcon fontSize="small" />, text: 'Not saved', color: 'error.main' },
  }[saveState];

  return (
    <>
      {/*
        Sticky context. The old form paginated five questions at a time with the
        patient named once at the top, so it was easy to lose track of who and
        where you were.
      */}
      <Box
        sx={{
          position: 'sticky',
          top: layout.appBarHeight,
          zIndex: 2,
          mx: { xs: -2, sm: -3 },
          px: { xs: 2, sm: 3 },
          py: 1.5,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton size="small" onClick={() => navigate(`/patient/${patientId}/assess`)} aria-label="Back to domains">
            <ArrowBackIcon fontSize="small" />
          </IconButton>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h4" noWrap>{domain.name}</Typography>
              <Chip size="small" label={code} sx={{ bgcolor: 'grey.100' }} />
              <Chip
                size="small"
                variant="outlined"
                color={lastAssessedAt ? 'default' : 'success'}
                label={lastAssessedAt ? 'Follow-up' : 'First assessment'}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap>
              {patient?.name}
              {patient?.dateOfBirth && ` · ${formatAge(patient.dateOfBirth)}`}
              {' · '}{answeredCount} of {questions.length} answered
              {!isConcernDomain && ` · ${achievedCount} achieved`}
            </Typography>
          </Box>

          {saveIndicator && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: saveIndicator.color, flexShrink: 0 }}>
              {saveIndicator.icon}
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {saveIndicator.text}
              </Typography>
            </Stack>
          )}

          <Tooltip title="Keyboard shortcuts">
            <IconButton size="small" onClick={() => setShowKeys((s) => !s)} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              <KeyboardIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button variant="contained" onClick={finish} sx={{ flexShrink: 0 }}>
            Save &amp; close
          </Button>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 1.5, height: 4, borderRadius: 2, bgcolor: 'grey.100' }}
        />
      </Box>

      {showKeys && (
        <Alert severity="info" onClose={() => setShowKeys(false)} sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>1</strong> Yes · <strong>2</strong> Emerging · <strong>3</strong> No — scores the highlighted
            box and moves on. <strong>↑ ↓</strong> change question, <strong>← →</strong> switch between clinician
            and parent.
          </Typography>
        </Alert>
      )}

      {isConcernDomain && (
        <Alert severity="info" sx={{ mt: 2 }}>
          In {domain.name}, a <strong>Yes from both</strong> is recorded as a concern in the progress report.
        </Alert>
      )}

      <Stack spacing={3} sx={{ mt: 3, pb: 6 }}>
        {groups.map((group, gi) => {
          const tone = group.level ? levelTokens[group.level] : null;
          const groupAnswered = group.items.filter(({ q }) => {
            const r = responses[q.id];
            return r && r.answer !== undefined && r.pr !== undefined;
          }).length;
          const complete = groupAnswered === group.items.length;

          return (
            <Box key={group.level ?? `g${gi}`}>
              {group.level && (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Typography variant="overline" sx={{ color: tone?.main }}>
                    {tone?.label || `Level ${group.level}`}
                  </Typography>
                  <Divider sx={{ flexGrow: 1 }} />
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {complete && <CheckCircleIcon fontSize="small" color="success" />}
                    <Typography variant="caption" color="text.secondary">
                      {groupAnswered}/{group.items.length}
                    </Typography>
                  </Stack>
                </Stack>
              )}

              <Stack spacing={1.5}>
                {group.items.map(({ q, index }) => (
                  <QuestionRow
                    key={q.id}
                    ref={(el) => { rowRefs.current[index] = el; }}
                    question={q}
                    number={index + 1}
                    level={group.level}
                    value={responses[q.id] || {}}
                    cursorField={cursor.index === index ? cursor.field : null}
                    isConcernDomain={isConcernDomain}
                    onScore={(field, v) => { setScore(q.id, field, v); setCursor({ index, field }); advance(index, field); }}
                    onMirror={() => mirror(q.id)}
                    onFocusField={(field) => setCursor({ index, field })}
                  />
                ))}
              </Stack>
            </Box>
          );
        })}

        {questions.length === 0 && (
          <Alert severity="warning">This domain has no questions configured.</Alert>
        )}
      </Stack>

      {/*
        Navigate only after the dialog has fully closed. Navigating while it was
        still open unmounted it mid-flight and left MUI's scroll lock
        (body overflow: hidden) behind, so the next page could not scroll.
      */}
      <Dialog
        open={doneOpen}
        onClose={ending ? undefined : () => setDoneOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionProps={{
          onExited: () => {
            const to = pendingNavRef.current;
            pendingNavRef.current = null;
            if (to) navigate(to);
          },
        }}
      >
        {doneStep === 'saved' ? (
          <>
            <DialogTitle>{domain.name} saved</DialogTitle>
            <DialogContent>
              <DialogContentText variant="body2">
                {answeredCount} of {questions.length} questions answered
                {!isConcernDomain && `, ${achievedCount} skills achieved`}.
                You can return to this domain at any time.
              </DialogContentText>
              {endsAssessment && !nextDomain && (
                <DialogContentText variant="body2" sx={{ mt: 1 }}>
                  All domains are scored. You can finish the assessment and view the report.
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              {nextDomain ? (
                <Button onClick={() => closeDoneThen(`/patient/${patientId}/assess/${nextDomain.code}`)}>
                  Next: {nextDomain.name}
                </Button>
              ) : (
                <Button onClick={() => closeDoneThen(`/patient/${patientId}/assess`)}>
                  All domains
                </Button>
              )}
              <Button variant="contained" onClick={onViewReport}>
                {endsAssessment ? 'Finish & view report' : 'View report'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>End this assessment?</DialogTitle>
            <DialogContent>
              <DialogContentText variant="body2">
                {scoredCount} of {categories.length || 10} domains scored.
              </DialogContentText>
              <Alert severity={scoredCount < (categories.length || 10) ? 'warning' : 'info'} sx={{ mt: 2 }}>
                Viewing the progress report ends this paid assessment. You won't be able to score
                more domains, and the next assessment will need a new payment.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDoneStep('saved')} disabled={ending}>
                Back
              </Button>
              <Button variant="contained" color="warning" onClick={endAssessmentAndOpenReport} disabled={ending}>
                {ending ? 'Ending…' : 'End assessment & view report'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

/**
 * Remount the screen per domain. "Next: <domain>" navigates between
 * /assess/:code URLs on the same route; without a fresh instance the previous
 * domain's answers would stay in state and be saved under the next domain.
 */
const DomainForm = () => {
  const { patientId, code } = useParams();
  return <DomainFormScreen key={`${patientId}:${code}`} />;
};

export default DomainForm;
